/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

import de from '../i18n/de.json';
import en from '../i18n/en.json';
import fr from '../i18n/fr.json';

/*
 * Interface strings.
 *
 * These are the Map Viewer's own chrome — button names, accessibility labels,
 * the cookie dialog's fixed text. They used to be extracted from the Jinja
 * template into Flask-Babel catalogs and shipped inside the configuration
 * payload, which meant adding a button label needed a backend change.
 *
 * What stays on the backend is everything a tenant can override: the map
 * labels an editor sets (`get_label`), the cookie descriptions stored per
 * map, and the map's own content. Those still arrive in the configuration,
 * because they are data, not interface.
 *
 * German is the source language, so a missing translation renders as the
 * German string rather than as a key.
 */

const CATALOGS = { de, en, fr };

let active = de;
let activeCode = 'de';

/**
 * Choose the language for interface strings.
 *
 * Falls back to the language without its region, then to German.
 *
 * @param {string} code - A language code, such as `de_CH` or `fr`.
 * @returns {string} The code that was actually selected.
 */
export function setLanguage(code) {
    const wanted = String(code || '').replace('-', '_');
    const candidates = [wanted, wanted.split('_')[0], 'de'];
    activeCode = candidates.find((c) => CATALOGS[c]) || 'de';
    active = CATALOGS[activeCode];
    return activeCode;
}

/** The language currently in use. */
export function language() {
    return activeCode;
}

/**
 * Read an interface string.
 *
 * Placeholders are `{name}`, not `%(name)s`: these strings are substituted
 * here rather than by `gettext`, which would raise on a `%(name)s` it was
 * given no arguments for.
 *
 * @param {string} path - A dotted key, such as `a11y.zoomLevel`.
 * @param {object} [values] - Values for the string's placeholders.
 * @returns {string} The string, or the key if there is no such string.
 */
export function t(path, values = {}) {
    const found = path
        .split('.')
        .reduce((node, key) => (node ? node[key] : undefined), active);

    if (typeof found !== 'string') {
        console.warn(`Missing interface string: ${path}`);
        return path;
    }
    return found.replace(/\{(\w+)\}/g, (match, name) =>
        name in values ? String(values[name]) : match
    );
}
